import ContentContainer from "Components/Container/ContentContainer";
import { SubDescription, SubTitle } from "Components/CustomComponents";
import CustomLoader from "Components/CustomComponents/CustomLoader";
import NoDataFound from "Components/CustomComponents/NoDataFound";
import CustomInfiniteScroll from "Components/CustomInfiniteScroll";
import ParserModal from "Components/Modals/ParserModal";
import { Routes } from "Data/interfaces";
import { useLazyGetRoutesQuery } from "Data/services/routesApi";
import moment from "moment";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { Col, Row, Spinner } from "react-bootstrap";
import { FaUpload } from "react-icons/fa6";
import { useNavigate } from "react-router-dom";
import { WEB_ROUTES } from "../../constants";
import RoundComponent from "./component/RouteComponent";
import styles from "./styles/Routes.module.scss";

const RouteScreen: React.FC = () => {
  const navigate = useNavigate();
  const dateInputRef = useRef<HTMLInputElement>(null);
  const [parserModal, setParserModal] = useState(false);
  const [selectedDate, setSelectedDate] = useState<string>(
    moment().format("YYYY-MM-DD")
  );

  // Pagination State
  const [routes, setRoutes] = useState<Routes[]>([]);
  const [page, setPage] = useState<number>(1);
  const [hasMore, setHasMore] = useState<boolean>(true);
  const [uploadLoading, setUploadLoading] = useState(false);
  const [getRoutes, { isFetching }] = useLazyGetRoutesQuery();

  // Fetch Routes on Date Change
  useEffect(() => {
    setPage(1);
    setRoutes([]);
    getRoutes({ date: selectedDate, page: 1 }).then(({ data }) => {
      setRoutes(data?.data || []);
      setHasMore(data?.links.total !== data?.links.current);
    });
  }, [selectedDate, uploadLoading]);

  // Fetch More Routes for Infinite Scroll
  const fetchMoreRoutes = async () => {
    if (!hasMore || isFetching) return;

    const nextPage = page + 1;
    try {
      const response = await getRoutes({ date: selectedDate, page: nextPage });
      const data = response?.data; // Ensure data is accessed safely

      if (data !== undefined && data?.data?.length > 0) {
        setRoutes((prev) => [...prev, ...data.data]);
        setPage(nextPage);
        setHasMore(data.links.total !== data.links.current);
      } else {
        setHasMore(false);
      }
    } catch (error) {
      console.error("Error fetching more routes:", error);
    }
  };

  const handleImageClick = () => {
    if (dateInputRef.current) {
      dateInputRef.current.showPicker?.();
    }
  };

  const handleDateChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSelectedDate(event.target.value);
  };

  const getDateLabel = useMemo(() => {
    const today = moment().startOf("day");
    const selected = moment(selectedDate).startOf("day");

    if (selected.isSame(today, "day")) return "Today";
    if (selected.isSame(today.subtract(1, "day"), "day")) return "Yesterday";

    return selected.format("MMM D, YYYY");
  }, [selectedDate]);

  return (
    <ContentContainer classes={`${styles.Routes} `} hasBoxShadow={true}>
      <div className="d-flex justify-content-between align-items-center">
        <SubTitle text="Route" classes="mb-2 fw-light" />
        <div className="d-flex gap-2 align-items-center">
          <div
            className="d-flex align-items-center gap-1 cursor-pointer"
            onClick={() => setParserModal(true)}
          >
            <FaUpload size={12} />
            <SubDescription text="Bulk Upload" classes="fs-7  text-blue" />
          </div>
          <div className="d-flex align-items-center">
            <input
              ref={dateInputRef}
              style={{ visibility: "hidden", width: "20px" }}
              type="date"
              name="selected-date"
              value={selectedDate}
              onChange={handleDateChange}
            />
            <img
              alt=""
              src="../images/calendarIcon.png"
              width={30}
              height={30}
              style={{ cursor: "pointer" }}
              onClick={handleImageClick}
            />
            <SubTitle text={getDateLabel} classes="fs-6 fw-light " />
          </div>
        </div>
      </div>
      <section className="my-3 d-flex gap-0 gap-sm-3  justify-content-center justify-content-md-start flex-wrap">
        <Row className="w-100">
          {isFetching && routes.length === 0 ? (
            <CustomLoader />
          ) : routes?.length === 0 ? (
            <NoDataFound />
          ) : (
            <CustomInfiniteScroll
              loader={
                <div className="flex items-center justify-center">
                  <Spinner animation="border" />
                </div>
              }
              getRecords={fetchMoreRoutes}
              hasMore={hasMore}
            >
              {routes.map((route) => {
                return (
                  <Col
                    sm={12}
                    md={6}
                    lg={4}
                    xl={3}
                    className="mb-3 mb "
                    key={route.id}
                  >
                    <div
                      onClick={() =>
                        navigate(WEB_ROUTES.ROUTE_DETAIL(route.id as number), {
                          state: { selectedDate },
                        })
                      }
                      style={{ cursor: "pointer" }}
                    >
                      <RoundComponent
                        round={route.route_name}
                        currentRound={route.remaining_stops}
                        totalRound={route.total_stops}
                      />
                    </div>
                  </Col>
                );
              })}
            </CustomInfiniteScroll>
          )}
        </Row>
      </section>
      {parserModal ? (
        <ParserModal
          handleParserModalClose={() => setParserModal(false)}
          getRoutes={() => getRoutes({ date: selectedDate, page: 1 })}
          setUploadLoading={setUploadLoading}
          uploadLoading={uploadLoading}
        />
      ) : null}
    </ContentContainer>
  );
};

export default RouteScreen;
